// Expirations: every candidate's license and CPR expiration from the onboarding
// documents, soonest first, red at <= 30 days, filterable by discipline and
// recruiter. There is deliberately no export, download or copy. One write: "Record renewal" on a row (hub ruling
// 2026-10-10) -- the new date, and the candidate's renewal file if they replied with one (lib/expirations.js).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RESTRICTED, RED_DAYS } from './lib/onboarding';
import { KIND_LABELS, shape, applyFilters, options, daysText, ANY, renewalProblem } from './lib/expirations';

const sel = { border: '1px solid #cbd5e1', borderRadius: 8, padding: '6px 10px', fontSize: 13, background: '#fff' };
const smallBtn = (bg, fg) => ({ background: bg, color: fg, border: 0, borderRadius: 6, padding: '6px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' });
const rowKey = (r) => `${r.candidate_id}-${r.kind}-${r.license_state || ''}`;

/** The inline form under a row: the new date (required) and the renewal file (optional). */
function RenewalForm({ row, busy, error, onSave, onCancel }) {
  const [date, setDate] = useState('');
  const [file, setFile] = useState(null);
  const id = `renew-${rowKey(row)}`;
  return (
    <div data-testid="exp-renew-form" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', padding: '8px 8px 12px' }}>
      <label htmlFor={`${id}-date`} style={{ fontSize: 12, color: '#475569' }}>New expiration date<br />
        <input id={`${id}-date`} data-testid="exp-renew-date" type="date" value={date} disabled={busy} onChange={(e) => setDate(e.target.value)} style={sel} />
      </label>
      <label htmlFor={`${id}-file`} style={{ fontSize: 12, color: '#475569' }}>Renewal file (optional): PDF, JPG or PNG<br />
        <input id={`${id}-file`} data-testid="exp-renew-file" type="file" accept="application/pdf,image/jpeg,image/png" disabled={busy}
          onChange={(e) => setFile((e.target.files && e.target.files[0]) || null)} style={{ fontSize: 12 }} />
      </label>
      <button className="nb" data-testid="exp-renew-save" disabled={busy} onClick={() => onSave({ expiresOn: date, file })} style={smallBtn('#0f766e', 'white')}>
        {busy ? 'Saving…' : 'Save renewal'}</button>
      <button className="nb" data-testid="exp-renew-cancel" disabled={busy} onClick={onCancel} style={smallBtn('#f1f5f9', '#334155')}>Cancel</button>
      {error && <p data-testid="exp-renew-error" role="alert" style={{ flexBasis: '100%', margin: 0, fontSize: 12, color: '#b91c1c' }}>{error}</p>}
    </div>
  );
}

export default function ExpirationsView({ api, candidates, onOpenCandidate = () => {}, staffEmail = '', now = () => new Date() }) {
  const [state, setState] = useState({ loading: true });
  const [discipline, setDiscipline] = useState(ANY);
  const [recruiter, setRecruiter] = useState(ANY);
  const [renewing, setRenewing] = useState(null);     // the row key whose form is open
  const [renewBusy, setRenewBusy] = useState(false);
  const [renewError, setRenewError] = useState('');
  const [renewDone, setRenewDone] = useState('');

  const load = useCallback(() => api.load().then((r) => setState({ loading: false, ...r }), (e) => setState({ loading: false, error: e.message })), [api]);
  useEffect(() => { load(); }, [load]);

  const saveRenewal = async (row, input) => {
    const bad = renewalProblem(row, input, now());
    if (bad) { setRenewError(bad); return; }
    setRenewBusy(true); setRenewError('');
    try {
      const { mode } = await api.recordRenewal(row, { ...input, by: staffEmail }, now());
      setRenewing(null);
      setRenewDone(`Renewal recorded for ${row.candidate_name}: expires ${input.expiresOn}${mode === 'file' ? ', file attached' : ''}.`);
      await load();
    } catch (e) {
      setRenewError(e.message);
    } finally { setRenewBusy(false); }
  };

  const rows = useMemo(() => (state.allowed ? shape(state.rows, candidates) : []), [state, candidates]);
  const shown = applyFilters(rows, { discipline, recruiter });
  const redCount = shown.filter((r) => r.red).length;

  if (state.loading) return <p style={{ color: '#64748b' }}>Loading expirations…</p>;
  if (state.error) return <p data-testid="exp-error" style={{ color: '#b91c1c' }}>{state.error}</p>;

  return (
    <div data-testid="expirations-view">
      <h2 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', margin: '0 0 4px' }}>Expirations</h2>
      <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 14px' }}>Licenses and CPR cards from onboarding documents. Red at {RED_DAYS} days or fewer.</p>
      {!state.allowed && <p data-testid="exp-restricted" style={{ color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: 10 }}>{RESTRICTED}</p>}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <label style={{ fontSize: 13 }}>Discipline{' '}
          <select data-testid="exp-discipline" style={sel} value={discipline} disabled={!state.allowed} onChange={(e) => setDiscipline(e.target.value)}>
            <option value={ANY}>All</option>
            {options(rows, 'discipline').map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 13 }}>Recruiter{' '}
          <select data-testid="exp-recruiter" style={sel} value={recruiter} disabled={!state.allowed} onChange={(e) => setRecruiter(e.target.value)}>
            <option value={ANY}>All</option>
            {options(rows, 'recruiter').map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        {state.allowed && <span data-testid="exp-count" style={{ fontSize: 13, color: '#475569' }}>{shown.length} shown · <b style={{ color: '#b91c1c' }}>{redCount} at {RED_DAYS} days or fewer</b></span>}
      </div>
      {renewDone && <p data-testid="exp-renew-done" role="status" style={{ fontSize: 13, color: '#166534', margin: '0 0 10px' }}>{renewDone}</p>}
      {state.allowed && shown.length === 0 && <p data-testid="exp-empty" style={{ color: '#64748b', fontSize: 13 }}>Nothing to show.</p>}
      {state.allowed && shown.length > 0 && (
        <table data-testid="exp-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, background: '#fff' }}>
          <thead><tr style={{ textAlign: 'left', color: '#64748b' }}>
            <th style={{ padding: 8 }}>Candidate</th><th>Discipline</th><th>Recruiter</th><th>Document</th><th>Expires</th><th>Days</th><th></th>
          </tr></thead>
          <tbody>
            {shown.map((r) => (<React.Fragment key={rowKey(r)}>
              <tr data-testid="exp-row" data-red={r.red ? 'yes' : 'no'}
                style={{ borderTop: '1px solid #e2e8f0', background: r.red ? '#fef2f2' : undefined }}>
                <td style={{ padding: 8 }}>
                  <button className="nb" style={{ color: '#1d4ed8', fontWeight: 600, background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
                    onClick={() => onOpenCandidate(r.candidate_id)}>{r.candidate_name}</button>
                </td>
                <td>{r.discipline}</td>
                <td>{r.recruiter}</td>
                <td>{KIND_LABELS[r.kind] || r.kind}{r.license_state ? ` · ${r.license_state}` : ''}</td>
                <td>{r.expires_on}</td>
                <td style={{ color: r.red ? '#b91c1c' : '#475569', fontWeight: r.red ? 700 : 400 }}>{daysText(r.days)}</td>
                <td style={{ textAlign: 'right', paddingRight: 8 }}>
                  {renewing !== rowKey(r) && (
                    <button className="nb" data-testid="exp-renew" disabled={renewBusy}
                      onClick={() => { setRenewing(rowKey(r)); setRenewError(''); setRenewDone(''); }} style={smallBtn('#ecfeff', '#0e7490')}>Record renewal</button>
                  )}
                </td>
              </tr>
              {renewing === rowKey(r) && (
                <tr data-testid="exp-renew-row"><td colSpan={7}>
                  <RenewalForm row={r} busy={renewBusy} error={renewError} onSave={(input) => saveRenewal(r, input)}
                    onCancel={() => { setRenewing(null); setRenewError(''); }} />
                </td></tr>
              )}
            </React.Fragment>))}
          </tbody>
        </table>
      )}
    </div>
  );
}
